import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { rtdb, storage } from "../firebase";
import {
  ref as dbRef,
  get,
  remove as dbRemove,
} from "firebase/database";
import { ref as storageRef, deleteObject } from "firebase/storage";
import MonthAccordion from "./MonthAccordion";
import "./AdminData.css";

export default function AdminData({ onBack }) {
  const possibleImageKeys = ["imageUrl", "qrImageUrl", "checklistImageUrl", "quantityImageUrl"];

  const firstLoad = useRef(true);
  const [grouped, setGrouped] = useState({});
  const [monthTotals, setMonthTotals] = useState({});
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonthsCount, setSelectedMonthsCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [expandAll, setExpandAll] = useState(false);

  // --- Logic Helpers ---
  const parseDateToTimestamp = (dateStr) => {
    if (!dateStr) return 0;
    const parts = dateStr.split("/");
    if (parts.length !== 3) return 0;
    const [d, m, y] = parts.map((p) => parseInt(p, 10));
    const dt = new Date(y, m - 1, d);
    return isNaN(dt.getTime()) ? 0 : dt.getTime();
  };

  const formatTimestamp = (ts) =>
    ts && ts > 0 ? new Date(ts).toLocaleDateString("en-GB") : "Unknown Date";

  // ⚡ OPTIMIZATION: One-time hit loading instead of a continuous active data leak stream
  // Wrapped in useCallback to preserve reference identity and resolve ESLint hook dependency rules safely
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await get(dbRef(rtdb, "Receive"));
      const groupedMap = { Unknown: [] };
      const totals = {};

      if (snapshot.exists()) {
        snapshot.forEach((child) => {
          const id = child.key;
          const fgNumber = child.child("fgNumber").val() || id || "UNKNOWN";
          const dateStr = child.child("receivingDate").val();
          const ts = parseDateToTimestamp(dateStr);

          if (ts <= 0) {
            groupedMap["Unknown"].push({ fgNumber, timestamp: 0, id });
            totals["Unknown"] = (totals["Unknown"] || 0) + 1;
            return;
          }

          const date = new Date(ts);
          const monthKey = `${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
          
          const dayOfMonth = date.getDate();
          const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
          const firstDayWeekday = (firstDayOfMonth.getDay() + 6) % 7;
          const firstMonday = firstDayWeekday === 0 ? 1 : 8 - firstDayWeekday;
          const weekNumberInMonth = Math.floor((dayOfMonth - firstMonday + 7) / 7) + 1;
          const weekKey = `Week-${weekNumberInMonth}`;
          const dayKey = date.toLocaleDateString("en-GB");

          if (!groupedMap[monthKey]) groupedMap[monthKey] = {};
          if (!groupedMap[monthKey][weekKey]) groupedMap[monthKey][weekKey] = {};
          if (!groupedMap[monthKey][weekKey][dayKey]) groupedMap[monthKey][weekKey][dayKey] = [];

          groupedMap[monthKey][weekKey][dayKey].push({ fgNumber, timestamp: ts, id });
          totals[monthKey] = (totals[monthKey] || 0) + 1;
        });
      }

      if (groupedMap["Unknown"].length === 0) delete groupedMap["Unknown"];

      // Sort chronological blocks efficiently
      for (const monthKey of Object.keys(groupedMap)) {
        const month = groupedMap[monthKey];
        if (Array.isArray(month)) continue;
        const sortedWeeks = {};
        Object.keys(month)
          .sort((a, b) => parseInt(b.replace("Week-", "")) - parseInt(a.replace("Week-", "")))
          .forEach((wk) => {
            const sortedDays = {};
            Object.keys(month[wk])
              .sort((a, b) => parseDateToTimestamp(b) - parseDateToTimestamp(a))
              .forEach((dk) => { sortedDays[dk] = month[wk][dk]; });
            sortedWeeks[wk] = sortedDays;
          });
        groupedMap[monthKey] = sortedWeeks;
      }

      const monthKeys = Object.keys(groupedMap)
        .filter((k) => k !== "Unknown")
        .sort((a, b) => {
          const [am, ay] = a.split("-").map(Number);
          const [bm, by] = b.split("-").map(Number);
          return new Date(by, bm - 1, 1) - new Date(ay, am - 1, 1);
        });
      if (groupedMap["Unknown"]) monthKeys.push("Unknown");

      setGrouped(groupedMap);
      setMonthTotals(totals);
      setAvailableMonths(monthKeys);
      if (firstLoad.current) { setSelectedMonthsCount(1); firstLoad.current = false; }
    } catch (err) {
      console.error("Failed loading structural metadata:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const totalItemsCount = useMemo(() => 
    Object.values(monthTotals).reduce((a, b) => a + b, 0), [monthTotals]);

  // ⚡ OPTIMIZATION: Kill heavy object queries. Safely delete by exact matching key targets
  const safelyDeleteDirectRecord = async (nodeName, fgNumber) => {
    try {
      const targetedNodeRef = dbRef(rtdb, `${nodeName}/${fgNumber}`);
      const snap = await get(targetedNodeRef);
      if (!snap.exists()) return null;

      const imageCleanupPromises = [];
      possibleImageKeys.forEach((key) => {
        const url = snap.child(key).val();
        if (url) {
          imageCleanupPromises.push(deleteObject(storageRef(storage, url)).catch(() => {}));
        }
      });

      await Promise.all(imageCleanupPromises);
      return dbRemove(targetedNodeRef);
    } catch (e) {
      return null;
    }
  };

  // ⚡ FAST STORAGE CLEANUP: Skip dangerous listAll requests entirely by targeting predictable image paths
  const deletePredictedImages = async (fgNumber) => {
    const directStoragePaths = [
      `receives/${fgNumber}.jpg`,
      `qr_codes/${fgNumber}.png`,
      `qr_codes_treats/${fgNumber}.png`,
      `qr_codes_paints/${fgNumber}.png`,
      `qr_codes_packs/${fgNumber}.png`,
      `qr_codes_delivers/${fgNumber}.png`
    ];
    return Promise.all(directStoragePaths.map(path => deleteObject(storageRef(storage, path)).catch(() => {})));
  };

  const flattenMonthItems = (month) => {
    const items = [];
    const m = grouped[month];
    if (!m) return items;
    if (Array.isArray(m)) return m;
    Object.values(m).forEach(week => Object.values(week).forEach(dayArr => items.push(...dayArr)));
    return items;
  };

  const executeDeletion = async (targetMonths, isUnknown = false) => {
    setWorking(true);
    setMessage(isUnknown ? "Clearing Unknowns..." : "Purging Database...");
    const safetySwitch = setTimeout(() => { setWorking(false); setMessage(""); }, 45000);

    try {
      const allTasks = [];
      for (const month of targetMonths) {
        const items = isUnknown ? (grouped["Unknown"] || []) : flattenMonthItems(month);
        for (const item of items) {
          const { fgNumber, id } = item;
          
          // Delete sequentially across targeted node addresses instead of querying
          allTasks.push(dbRemove(dbRef(rtdb, `Receive/${id}`)));
          allTasks.push(dbRemove(dbRef(rtdb, `Progress/${fgNumber}`))); // Keep progress clean
          
          ["Treat", "Paint", "Pack", "Deliver"].forEach(node => {
            allTasks.push(safelyDeleteDirectRecord(node, fgNumber));
          });
          
          allTasks.push(deletePredictedImages(fgNumber));
        }
      }
      
      await Promise.all(allTasks);
      clearTimeout(safetySwitch);
      setMessage("Success!");
      setTimeout(() => { setWorking(false); setMessage(""); loadInitialData(); }, 2000);
    } catch (e) {
      clearTimeout(safetySwitch);
      setMessage("Purge execution error.");
      setTimeout(() => setWorking(false), 2000);
    }
  };

  const handleDeleteChoice = () => {
    const choice = window.prompt("1 = Delete by months\n2 = Delete Unknowns\n\nEnter 1 or 2");
    if (choice === "1" && selectedMonthsCount > 0) {
      const target = availableMonths.slice(-selectedMonthsCount);
      if (window.confirm(`Delete: ${target.join(", ")}?`)) executeDeletion(target);
    } else if (choice === "2") {
      if (window.confirm("Delete all Unknowns?")) executeDeletion(["Unknown"], true);
    }
  };

  return (
    <div className="admin-data-root">
      <header className="admin-header">
        <h2>Data Management</h2>
      </header>

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-value">{availableMonths.filter(m => m !== "Unknown").length}</span>
          <span className="stat-label-data">Active Months</span>
        </div>
        <div className="stat-card primary">
          <span className="stat-value">{totalItemsCount}</span>
          <span className="stat-label-data">Total Records</span>
        </div>
        <div className="stat-card warning">
          <span className="stat-value">{monthTotals["Unknown"] || 0}</span>
          <span className="stat-label-data">Incomplete Dates</span>
        </div>
      </div>

      <div className="accordion-card">
        <div className="accordion-toolbar">
          <button className="icon-btn expand" onClick={() => setExpandAll(true)}>Expand All</button>
          <button className="icon-btn collapse" onClick={() => setExpandAll(false)}>Collapse All</button>
        </div>

        <div className="scroll-area">
          {loading ? (
            <div className="loader-container"><p className="loading-text">Analyzing Database</p></div>
          ) : availableMonths.length === 0 ? (
            <div className="empty">No records found.</div>
          ) : (
            availableMonths.map((monthKey) => (
              <MonthAccordion
                key={monthKey}
                monthKey={monthKey}
                items={grouped[monthKey] || []}
                total={monthTotals[monthKey] || 0}
                formatTimestamp={formatTimestamp}
                expandAll={expandAll}
              />
            ))
          )}
        </div>
      </div>

      <div className="filter-card">
        <div className="filter-header">
          <label className="slider-label">Select Total Month To Delete</label>
          <span className="selection-badge">{selectedMonthsCount} Month(s) Selected</span>
        </div>
        <input
          type="range"
          min={availableMonths.length > 0 ? 1 : 0}
          max={availableMonths.length > 0 ? availableMonths.length : 0}
          value={selectedMonthsCount}
          onChange={(e) => setSelectedMonthsCount(Number(e.target.value))}
        />
        <p className="selected-months-text">
          Targeting: {availableMonths.slice(-selectedMonthsCount).join(", ")}
        </p>
        
        <div className="actions-row">
          <button className="delete-btn" onClick={handleDeleteChoice} disabled={working}>
            Cleanse Database
          </button>
        </div>
      </div>

      {working && message && (
        <div className="working-overlay">
          <div className="spinner"></div>
          {message}
        </div>
      )}
    </div>
  );
}